import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Select2Data, Select2Option } from 'ng-select2-component';
import { ParticipantesService } from './institucion.service';
import { UsuarioService } from '../usuarios/usuarios.service';
import { Institucion } from './institucion.model';
import { Usuario } from '../usuarios/usuarios.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CasosService } from '../casos/casos.service';
import { AuthenticationService } from '../core/service/auth.service';
import { getCurrentLanguage } from '../core/helpers/utils';

interface ParticipanteConDetalles extends Participante {
  nombre?: string;
  apellidos?: string;
  enviar_email_subida_archivo?: boolean;
  enviar_email_hito?: boolean;
}

interface Participante {
  id: string;
  id_usuario: string;
  tipo: string;
  fecha_designacion: string | null;
  usuario?: Usuario; // Aseguramos que usuario es opcional
  enviar_email_subida_archivo?: boolean;
  enviar_email_hito?: boolean;
}



@Component({
  selector: 'app-gestion-institucion',
  templateUrl: './gestion-institucion.component.html',
  styleUrls: ['./gestion-institucion.component.scss']
})
export class GestionInstitucionComponent implements OnInit {

  @Output() refreshCase = new EventEmitter<void>();

  usuarios: Select2Data = [];
  usuariosList: Usuario[] = [];
  institucionesList: ParticipanteConDetalles[] = [];
  avatarUrls: { [key: string]: SafeUrl } = {}; // Almacena las URLs de los avatares
  model: Institucion = {
    id_usuario: '',
    tipo: '',
    fecha_designacion: undefined
  };
  loaded = false;
  error = '';
  institucionesLoaded = false; // Controla la visibilidad del div de instituciones
  isLoading = true; // Controla la visibilidad del spinner
  estadoExpediente: string | undefined;

  disableOptions: boolean = true;

  casoCerrado: boolean = true;
  showConfirmDeleteModal: boolean = false;
  institucionToDelete: ParticipanteConDetalles | null = null;

  constructor(
    private participantesService: ParticipantesService,
    private usuarioService: UsuarioService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private casoService: CasosService,
    private authService: AuthenticationService
  ) { }

  ngOnInit(): void {

    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      // Saco el usuario logueado
      this.authService.currentUser().subscribe(user => {
        // Realiza cualquier otra operación que necesites con loggedInUser aquí dentro.
        if (user?.nivel_acceso) {
          // Si soy admin no deshabilito opciones
          if (user.nivel_acceso == "ADMIN") {
            this.disableOptions = false;
            this.loadUsuarios();
          }
          //else
          //  this.loaded = true;
        }
      }, error => {
        console.error('Error al obtener el usuario actual:', error);
        // Maneja el error, por ejemplo, mostrando un mensaje al usuario.
      });
      this.loadInstituciones();
    }
  }

  loadUsuarios(): void {
    this.usuarioService.getUsuarios('nivel_acceso=ADMIN&validado=true&activo=true').subscribe({
      next: (data) => {
        // Cargo los usuarios
        this.usuariosList = data;
        this.usuarios = this.usuariosList.map((user): Select2Option => ({
          value: user.id || '', // Ensure value is not undefined
          label: `${user.nombre} ${user.apellidos} - ${user.email} ${!user.validado ? '(No validado)' : ''}`
        }));
        this.loaded = true;
      },
      error: (error) => {
        this.error = error;
        this.loaded = false;
      }
    });
  }

  loadInstituciones(): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      this.casoService.getCaso(id_expediente).subscribe({
        next: (casoData) => {
          const participantes: Participante[] = casoData.participantes;
          this.estadoExpediente = casoData.estado;
          this.casoCerrado = this.isCaseClosed();
          this.loaded = true;

          // Filtrar y ordenar las instituciones
          this.institucionesList = participantes
            .filter((participante: Participante) =>
              participante.tipo === 'LETRADO' ||
              participante.tipo === 'ADMINISTRADOR' ||
              participante.tipo === 'SECRETARIA_GENERAL'
            )
            .map((participante: Participante): ParticipanteConDetalles => {
              const participanteConDetalles: ParticipanteConDetalles = { ...participante };

              // Si el usuario está asociado, rellenar los detalles
              if (participante.usuario) {
                participanteConDetalles.nombre = participante.usuario.nombre;
                participanteConDetalles.apellidos = participante.usuario.apellidos;
              }

              return participanteConDetalles;
            });

          if (this.institucionesList.length === 0) {
            this.institucionesLoaded = true;
            this.isLoading = false;
            return;
          }

          let avatarLoadCount = 0;
          this.institucionesList.forEach((participante) => {
            if (participante.id_usuario) {
              this.usuarioService.getAvatar(participante.id_usuario).subscribe(avatarBlob => {
                if (avatarBlob) {
                  const blobUrl = URL.createObjectURL(avatarBlob);
                  this.avatarUrls[participante.id_usuario] = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
                }
                avatarLoadCount++;
                if (avatarLoadCount === this.institucionesList.length) {
                  this.institucionesLoaded = true;
                  this.isLoading = false;
                }
              });
            } else {
              avatarLoadCount++;
              if (avatarLoadCount === this.institucionesList.length) {
                this.institucionesLoaded = true;
                this.isLoading = false;
              }
            }
          });
        },
        error: (error) => {
          this.error = error;
          this.isLoading = false;
        }
      });
    }
  }


  isCaseClosed(): boolean {
    const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
    return estadosCerrados.includes(this.estadoExpediente ?? '');
  }


  onSubmit(form: NgForm): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      const newInstitucion: Institucion = {
        id_expediente: id_expediente,
        id_usuario: form.value.id_usuario,
        tipo: form.value.tipo,
        fecha_designacion: new Date().toISOString().split('T')[0] // Asignar la fecha actual
      };

      this.isLoading = true; // Show spinner on submit
      this.participantesService.newInstitucion(newInstitucion).subscribe({
        next: () => {
          this.model = { id_usuario: '', tipo: '', fecha_designacion: undefined }; // Reset form model
          form.resetForm(); // Reset form validations
          this.loadInstituciones(); // Refresh the instituciones list
          this.refreshCase.emit();
        },
        error: (error) => {
          this.error = error;
          this.isLoading = false;
        }
      });
    }
  }



  openDeleteModal(institucion: ParticipanteConDetalles): void {
    this.institucionToDelete = institucion;
    this.showConfirmDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showConfirmDeleteModal = false;
    this.institucionToDelete = null;
  }

  confirmDelete(): void {
    if (this.institucionToDelete?.id) {
      this.deleteInstitucion(this.institucionToDelete.id, true);
    }
  }

  deleteInstitucion(id: string | undefined, closeModalOnSuccess: boolean = false): void {
    if (id) {
      this.isLoading = true; // Show spinner on delete
      this.participantesService.deleteInstitucion(id).subscribe({
        next: () => {
          if (closeModalOnSuccess) {
            this.closeDeleteModal();
          }
          this.loadInstituciones(); // Refresh the instituciones list
          this.refreshCase.emit();
        },
        error: (error) => {
          this.error = error;
          this.isLoading = false;
        }
      });
    }
  }

  onSwitchChange(id: string): void {
    const switchElement = document.getElementById(`switch_${id}`) as HTMLInputElement;
    if (switchElement) {
      const isChecked = switchElement.checked; // Estado del switch

      // Encuentra el tribunal correspondiente en la lista
      const institucionToUpdate = this.institucionesList.find(institucion => institucion.id === id);
      if (institucionToUpdate) {
        // Actualiza el campo enviar_email_subida_archivo
        institucionToUpdate.enviar_email_subida_archivo = isChecked;

        // Crear el objeto para la actualización
        const institucionUpdateData: Institucion = {
          id_usuario: institucionToUpdate.id_usuario,
          enviar_email_subida_archivo: isChecked // Nuevo valor del switch
        };

        // Llamar al servicio para actualizar en la base de datos
        this.participantesService.updateInstitucion(id, institucionUpdateData).subscribe({
          next: () => {
            //console.log(`Tribunal con ID ${id} actualizado con éxito. Estado enviar_email_subida_archivo: ${isChecked}`);
            // Puedes emitir eventos o recargar los datos si es necesario
            //this.refreshCase.emit();
          },
          error: (error) => {
            //console.error('Error updating tribunal:', error);
            this.error = error;
            this.loadInstituciones();
          }
        });
      } else {
        console.error(`No se encontró la institución con ID ${id} en la lista.`);
      }
    } else {
      console.error(`No se encontró el switch con ID: switch_${id}`);
    }
  }

  onSwitchChangeHitos(id: string): void {
    const switchElement = document.getElementById(`switchHito_${id}`) as HTMLInputElement;
    if (switchElement) {
      const isChecked = switchElement.checked; // Estado del switch

      // Encuentra el tribunal correspondiente en la lista
      const institucionToUpdate = this.institucionesList.find(institucion => institucion.id === id);
      if (institucionToUpdate) {
        // Actualiza el campo enviar_email_hito
        institucionToUpdate.enviar_email_hito = isChecked;

        // Crear el objeto para la actualización
        const institucionUpdateData: Institucion = {
          id_usuario: institucionToUpdate.id_usuario,
          enviar_email_hito: isChecked // Nuevo valor del switch
        };

        // Llamar al servicio para actualizar en la base de datos
        this.participantesService.updateInstitucion(id, institucionUpdateData).subscribe({
          next: () => {
            //console.log(`Tribunal con ID ${id} actualizado con éxito. Estado enviar_email_hito: ${isChecked}`);
            // Puedes emitir eventos o recargar los datos si es necesario
            //this.refreshCase.emit();
          },
          error: (error) => {
            //console.error('Error updating tribunal:', error);
            this.error = error;
            this.loadInstituciones();
          }
        });
      } else {
        console.error(`No se encontró la institución con ID ${id} en la lista.`);
      }
    } else {
      console.error(`No se encontró el switch con ID: switchHito_${id}`);
    }
  }

  traducirInstitucionTipo(tipo?: string | null): string {
    if (!tipo) {
      return '';
    }

    const language = getCurrentLanguage();
    const normalizado = tipo.replace(/_/g, ' ');
    if (language !== 'en') {
      return normalizado;
    }

    const traducciones: { [key: string]: string } = {
      'LETRADO': 'COUNSEL',
      'ADMINISTRADOR': 'ADMINISTRATOR',
      'SECRETARIA GENERAL': 'SECRETARY GENERAL'
    };

    return traducciones[normalizado.toUpperCase()] || normalizado;
  }

}
